import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HeroDither from '../components/shared/HeroDither';
import Reveal, { REVEAL_CSS } from '../components/shared/Reveal';
import useSeoMeta from '../components/shared/useSeoMeta';
import RelatedPagesLinks from '../components/shared/RelatedPagesLinks';

const IconArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconScan = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><path d="M7 12h10"/>
  </svg>
);

const IconForm = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M9 15h6M9 11h3"/>
  </svg>
);

const IconFlag = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><path d="M4 22V15"/>
  </svg>
);

const IconKanban = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="10" rx="1"/>
  </svg>
);

const PROBLEM_STATS = [
  { value: '88%', label: 'de las tarjetas de papel se descartan en la primera semana tras un evento.' },
  { value: '60-70%', label: 'de los contactos que haces nunca llegan a un CRM, la información se pierde en el camino.' },
  { value: '73%', label: 'de los leads nunca reciben ningún seguimiento, ni llamada, ni correo, ni mensaje.' },
];

const CAPTURE_CARDS = [
  {
    icon: <IconScan/>,
    title: 'Escaneo universal con IA',
    desc: 'Toma una foto a una tarjeta física o a un gafete de evento desde el Hub, en el celular o la tablet. En segundos quedan cargados nombre, cargo, empresa, teléfono y correo, sin digitar nada a mano.',
  },
  {
    icon: <IconForm/>,
    title: 'Identificación desde tu perfil',
    desc: 'Cada perfil digital incluye su propio formulario de contacto. Cuando alguien te deja su nombre, correo o teléfono al ver tu perfil, el lead queda registrado de inmediato, sin que tú hagas nada.',
  },
  {
    icon: <IconFlag/>,
    title: 'Atribución automática por evento',
    desc: 'Si estás trabajando una feria, lanzamiento o campaña puntual, cada lead que entra durante esa ventana de tiempo queda vinculado automáticamente a ese evento, sin etiquetarlo tú mismo.',
  },
  {
    icon: <IconKanban/>,
    title: 'Pipeline visual, no una planilla',
    desc: 'Cada contacto avanza por estados (Nuevo, Contactado, Calificado) en un tablero tipo kanban. Los leads que llevan más tiempo esperando respuesta quedan marcados como urgentes.',
  },
];

const SYNC_ITEMS = [
  { title: 'Google Sheets', desc: 'Cada lead nuevo aparece en tu planilla en tiempo real, sin exportar nada a mano.' },
  { title: 'Salesforce', desc: 'Sincroniza tus leads directo al CRM que ya usa tu equipo comercial.' },
  { title: 'API de solo lectura', desc: 'Conecta tus propios scripts o automatizaciones a los leads que capturas, con una llave propia.' },
  { title: 'Exportar a Excel', desc: 'Descarga tu lista completa de leads con un clic desde el panel, en el formato que ya usa tu equipo.' },
];

const FAQS = [
  { q: '¿Necesito instalar algo para escanear tarjetas?', a: 'No. El escaneo con IA funciona directo desde el navegador, en el Hub de tu cuenta, en el celular o la tablet que ya uses.' },
  { q: '¿Qué pasa si supero los 5 leads del plan gratuito?', a: 'Los leads siguen entrando, pero quedan bloqueados hasta que subas a Pro o empiece el mes siguiente.' },
  { q: '¿Puedo exportar mis leads en cualquier momento?', a: 'Sí, desde el panel de Leads puedes exportar a Excel con un clic, en cualquier plan.' },
];

const PAGE_TITLE = 'Captura de Leads en Eventos con IA | Contact';
const PAGE_DESCRIPTION = 'Escanea tarjetas con IA, identifica visitantes desde tu perfil digital y sincroniza tus leads con Google Sheets, Salesforce o tu propia API. Empieza gratis.';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const CapturaDeLeadsPage = () => {
  const navigate = useNavigate();
  useSeoMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: '/captura-de-leads' });

  return (
    <div className="cl-root">
      <HeroDither/>
      <header className="cl-nav">
        <div className="cl-nav-inner">
          <ContactLogo clickable/>
          <div className="cl-nav-actions">
            <button onClick={() => navigate('/')} className="cl-btn-back">
              <IconArrowLeft/> Volver
            </button>
            <button onClick={() => navigate('/comprar')} className="cl-btn-comprar-nav">Comprar</button>
            <button onClick={() => navigate('/login')} className="cl-btn-acceder">Acceder</button>
          </div>
        </div>
      </header>

      <section className="cl-hero">
        <div className="cl-container">
          <p className="cl-eyebrow">Captura de Leads</p>
          <h1 className="cl-h1">Cada contacto queda registrado. Ninguno se pierde.</h1>
          <p className="cl-lead">
            Hoy la mayoría de los contactos que haces en un evento o reunión nunca llegan a un CRM.
            Contact resuelve esto en el momento: cada escaneo, cada identificación y cada conversación
            queda guardada como un lead real, listo para que tu equipo le haga seguimiento.
          </p>
        </div>
      </section>

      <section className="cl-section">
        <div className="cl-container">
          <Reveal>
          <div className="cl-stats-grid">
            {PROBLEM_STATS.map((s) => (
              <div key={s.value} className="cl-stat">
                <span className="cl-stat-value">{s.value}</span>
                <p className="cl-stat-label">{s.label}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="cl-section">
        <div className="cl-container">
          <Reveal>
          <h2 className="cl-h2">Tres formas de entrar, un solo lugar donde revisarlos.</h2>
          <div className="cl-grid">
            {CAPTURE_CARDS.map((c) => (
              <div key={c.title} className="cl-card">
                <span className="cl-card-icon">{c.icon}</span>
                <h3 className="cl-card-title">{c.title}</h3>
                <p className="cl-card-desc">{c.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="cl-section">
        <div className="cl-container">
          <Reveal>
          <h2 className="cl-h2 cl-h2--tight">Tus leads no se quedan atrapados en Contact.</h2>
          <div className="cl-sync-grid">
            {SYNC_ITEMS.map((s) => (
              <div key={s.title} className="cl-sync-item">
                <h4 className="cl-sync-title">{s.title}</h4>
                <p className="cl-sync-desc">{s.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="cl-section">
        <div className="cl-container">
          <Reveal>
          <h2 className="cl-h2 cl-h2--tight">Empieza gratis, crece sin cambiar de herramienta.</h2>
          <div className="cl-plan-grid">
            <div className="cl-plan-card">
              <h4 className="cl-plan-name">Gratuito</h4>
              <p className="cl-plan-desc">Hasta 5 leads identificados por mes, sin costo y sin límite de tiempo.</p>
            </div>
            <div className="cl-plan-card cl-plan-card--highlight">
              <h4 className="cl-plan-name">Pro</h4>
              <p className="cl-plan-desc">Leads ilimitados, identificación avanzada y sincronización con tus herramientas.</p>
            </div>
            <div className="cl-plan-card">
              <h4 className="cl-plan-name">Enterprise</h4>
              <p className="cl-plan-desc">Lo del Pro, más ranking de equipo y atribución por campaña a nivel de cuenta completa.</p>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      <section className="cl-section">
        <div className="cl-container">
          <Reveal>
          <h2 className="cl-h2 cl-h2--tight">Preguntas frecuentes.</h2>
          <div className="cl-faq-list">
            {FAQS.map((f) => (
              <div key={f.q} className="cl-faq-item">
                <h4 className="cl-faq-q">{f.q}</h4>
                <p className="cl-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="cl-cta">
        <div className="cl-container cl-cta-inner">
          <h2 className="cl-h2">Empieza a capturar leads reales hoy.</h2>
          <p className="cl-p cl-cta-sub">Crea tu perfil gratis y pruébalo en tu próxima reunión o evento.</p>
          <div className="cl-cta-actions">
            <button onClick={() => navigate('/register')} className="cl-btn-pill">
              Comenzar gratis <IconArrowRight/>
            </button>
            <button onClick={() => navigate('/cotizar')} className="cl-btn-outline">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      <footer className="cl-footer">
        <div className="cl-container">
          <div className="cl-related-row">
            <RelatedPagesLinks currentPath="/captura-de-leads" linkClassName="cl-related-link" />
          </div>
          <div className="cl-footer-inner">
            <ContactLogo/>
            <span className="cl-footer-copy">© 2026 CONTACT Network SPA. Chile</span>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <style>{CSS}</style>
      <style>{REVEAL_CSS}</style>
    </div>
  );
};

const CSS = `
:root{
  --cl-bg:#F9F9F8;
  --cl-bg-w:#FFFFFF;
  --cl-text:#000000;
  --cl-muted:#4a4a4a;
  --cl-faint:#C0C0C0;
  --cl-border:#EEEEEC;
  --cl-border-dk:#CCCCCA;
}

.cl-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--cl-bg);color:var(--cl-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}
.nx-hero-dither{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:.27;-webkit-mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);transform:translateZ(0);will-change:transform}

.cl-nav{position:sticky;top:0;z-index:100;background:transparent}
.cl-nav-inner{max-width:900px;margin:0 auto;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between}
.cl-btn-back{display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--cl-muted);font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.cl-btn-back:hover{color:var(--cl-text)}
.cl-nav-actions{display:flex;align-items:center;gap:10px}
.cl-btn-acceder{display:inline-flex;align-items:center;background:var(--cl-text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
.cl-btn-acceder:hover{opacity:.82}
.cl-btn-comprar-nav{display:inline-flex;align-items:center;background:transparent;color:var(--cl-text);border:1.5px solid var(--cl-border-dk);border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s}
.cl-btn-comprar-nav:hover{border-color:var(--cl-text)}

.cl-container{max-width:900px;margin:0 auto;padding:0 40px}

.cl-hero{padding:72px 0 56px}
.cl-eyebrow{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;color:var(--cl-muted);text-transform:uppercase;letter-spacing:.08em;margin-bottom:20px}
.cl-h1{font-weight:300;font-size:clamp(32px,4.6vw,52px);line-height:1.15;letter-spacing:-.025em;color:var(--cl-text);margin-bottom:26px;max-width:720px}
.cl-lead{font-family:'Inter',system-ui,sans-serif;font-size:clamp(15px,1.6vw,17px);color:var(--cl-muted);line-height:1.75;max-width:600px}

.cl-section{padding:0 0 80px}
.cl-h2{font-weight:300;font-size:clamp(22px,2.6vw,30px);line-height:1.3;letter-spacing:-.02em;color:var(--cl-text);margin-bottom:32px;max-width:620px}
.cl-h2--tight{margin-bottom:28px}
.cl-p{font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--cl-muted);line-height:1.75;max-width:560px}

.cl-stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px;padding:36px 0;border-top:1px solid var(--cl-border);border-bottom:1px solid var(--cl-border)}
.cl-stat-value{font-weight:300;font-size:clamp(30px,3.6vw,42px);letter-spacing:-.02em;color:var(--cl-text);display:block;margin-bottom:8px}
.cl-stat-label{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--cl-muted);line-height:1.6;max-width:260px}

.cl-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:1px;background:var(--cl-border);border:1px solid var(--cl-border)}
.cl-card{background:var(--cl-bg-w);padding:28px}
.cl-card-icon{display:inline-flex;color:var(--cl-text);margin-bottom:14px}
.cl-card-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--cl-text);margin-bottom:8px;letter-spacing:-.01em}
.cl-card-desc{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--cl-muted);line-height:1.65}

.cl-sync-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
.cl-sync-item{border-top:2px solid var(--cl-text);padding-top:16px}
.cl-sync-title{font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:700;color:var(--cl-text);margin-bottom:6px}
.cl-sync-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--cl-muted);line-height:1.6}

.cl-plan-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.cl-plan-card{background:var(--cl-bg-w);border:1px solid var(--cl-border);border-radius:14px;padding:24px}
.cl-plan-card--highlight{border-color:var(--cl-text)}
.cl-plan-name{font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:700;color:var(--cl-text);margin-bottom:8px}
.cl-plan-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--cl-muted);line-height:1.6}

.cl-faq-list{display:flex;flex-direction:column;gap:22px;max-width:640px}
.cl-faq-item{border-top:1px solid var(--cl-border);padding-top:16px}
.cl-faq-q{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--cl-text);margin-bottom:8px}
.cl-faq-a{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--cl-muted);line-height:1.65}

.cl-cta{padding:0 0 80px}
.cl-cta-inner{background:var(--cl-bg-w);border:1px solid var(--cl-border);border-radius:24px;padding:60px 48px;text-align:center;display:flex;flex-direction:column;align-items:center}
.cl-cta-sub{text-align:center;margin:0 auto 32px}
.cl-cta-actions{display:flex;align-items:center;gap:14px}
.cl-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--cl-text);color:#fff;border:none;border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.cl-btn-pill:hover{opacity:.82;transform:translateY(-1px)}
.cl-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--cl-text);border:1.5px solid var(--cl-border-dk);border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.cl-btn-outline:hover{border-color:var(--cl-text);background:var(--cl-bg)}

.cl-related-row{display:flex;flex-wrap:wrap;gap:10px;padding:28px 0;border-top:1px solid var(--cl-border)}
.cl-related-link{background:none;border:1px solid var(--cl-border-dk);border-radius:100px;padding:7px 15px;color:var(--cl-muted);font-family:'Inter',system-ui,sans-serif;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
.cl-related-link:hover{color:var(--cl-text);border-color:var(--cl-text)}

.cl-footer-inner{display:flex;align-items:center;justify-content:space-between;padding:24px 0}
.cl-footer-copy{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:var(--cl-faint)}

@media(max-width:640px){
  .cl-nav-inner,.cl-container{padding:0 20px}
  .cl-nav-actions{gap:6px}
  .cl-btn-acceder,.cl-btn-comprar-nav{height:32px;padding:0 12px;font-size:12px}
  .cl-btn-back{font-size:0}
  .cl-hero{padding:48px 0 40px}
  .cl-section,.cl-cta{padding-bottom:56px}
  .cl-stats-grid{grid-template-columns:1fr;gap:20px}
  .cl-grid,.cl-sync-grid,.cl-plan-grid{grid-template-columns:1fr}
  .cl-cta-inner{padding:44px 22px}
  .cl-cta-actions{flex-direction:column;width:100%}
  .cl-cta-actions .cl-btn-pill,.cl-cta-actions .cl-btn-outline{width:100%;justify-content:center}
  .cl-footer-inner{flex-direction:column;gap:8px;text-align:center}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default CapturaDeLeadsPage;
